import axios from "axios";

const api = axios.create({
  baseURL: process.env.REACT_APP_BACKEND_URL,
  withCredentials: true,
});

export const openApi = axios.create({
  baseURL: process.env.REACT_APP_BACKEND_URL,
});

// JWT de acesso existe somente na memória desta aba. O cookie de renovação
// HttpOnly pertence ao backend e restaura a sessão após recarregar a página.
// Apaga também qualquer token legado salvo pelas versões anteriores.
let accessToken = null;
let sessionUnavailable = false;
let logoutInProgress = false;
try { localStorage.removeItem("token"); } catch (_) {}

export const getAccessToken = () => accessToken;

// Sincroniza logout entre abas sem compartilhar JWT no localStorage.
try {
  window.addEventListener("storage", event => {
    if (event.key !== "agilizachat:session-logout") return;
    logoutInProgress = true;
    sessionUnavailable = true;
    clearAccessToken();
    window.dispatchEvent(new Event("agilizachat:session-expired"));
  });
} catch (_) {}

let sessionRevision = 0;
let refreshInFlight = null;

export const setAccessToken = token => {
  if (!token || typeof token !== "string") return;
  sessionRevision += 1;
  accessToken = token;
  sessionUnavailable = false;
  logoutInProgress = false;
  try { localStorage.removeItem("token"); } catch (_) {}
  api.defaults.headers.common.Authorization = `Bearer ${token}`;
  window.dispatchEvent(new Event("agilizachat:token-updated"));
};

export const clearAccessToken = () => {
  sessionRevision += 1;
  accessToken = null;
  try { localStorage.removeItem("token"); } catch (_) {}
  delete api.defaults.headers.common.Authorization;
  delete api.defaults.headers.Authorization;
  window.dispatchEvent(new Event("agilizachat:token-cleared"));
};

// Chamadas simultâneas com token vencido compartilham UMA renovação.
// Nunca renovar um token depois de logout/troca de usuário.
export const refreshSession = () => {
  if (logoutInProgress) return Promise.reject(new Error("ERR_AUTH_LOGOUT_IN_PROGRESS"));
  if (refreshInFlight) return refreshInFlight;
  const revisionAtStart = sessionRevision;
  const request = api.post("/auth/refresh_token", null, {
    skipAuthRefresh: true,
    skipAuthWait: true,
  }).then(({ data }) => {
    if (revisionAtStart !== sessionRevision) {
      throw new Error("ERR_AUTH_SESSION_CHANGED");
    }
    if (!data || !data.token) {
      throw new Error("ERR_AUTH_REFRESH_NO_TOKEN");
    }
    setAccessToken(data.token);
    return data;
  }).catch(error => {
    // Não apagar uma sessão nova por causa da resposta de uma requisição antiga.
    if (revisionAtStart === sessionRevision && error?.response?.status === 401) {
      sessionUnavailable = true;
      clearAccessToken();
      window.dispatchEvent(new Event("agilizachat:session-expired"));
    }
    throw error;
  });
  refreshInFlight = request;
  request.finally(() => { if (refreshInFlight === request) refreshInFlight = null; }).catch(() => {});
  return request;
};

// Logout aguarda uma eventual resposta de renovação para que nenhum
// Set-Cookie atrasado restaure a sessão depois da limpeza no servidor.
export const closeSession = async () => {
  logoutInProgress = true;
  // Apenas um sinal de logout, sem credenciais, para outras abas do navegador.
  try { localStorage.setItem("agilizachat:session-logout", String(Date.now())); } catch (_) {}
  const pending = refreshInFlight;
  clearAccessToken();
  if (pending) {
    try { await pending; } catch (_) {}
  }
  return api.delete("/auth/logout", {
    skipAuthRefresh: true,
    skipAuthWait: true,
  });
};

const authEndpoint = url => /\/auth\/(login|refresh_token|logout)(?:\?|$)/.test(String(url || ""));

// Instalados apenas uma vez, nunca durante uma renderização do React.
api.interceptors.request.use(async config => {
  const url = String(config.url || "");
  // Garante a recuperação da sessão ANTES das consultas protegidas:
  // os componentes antigos podem disparar suas requisições antes do useAuth.
  if (!config.skipAuthWait && !authEndpoint(url)) {
    if (logoutInProgress) throw new Error("ERR_AUTH_LOGOUT_IN_PROGRESS");
    if (refreshInFlight) await refreshInFlight;
    else if (!getAccessToken() && !sessionUnavailable) await refreshSession();
  }
  if (!authEndpoint(url) || /\/auth\/logout/.test(url)) {
    const token = getAccessToken();
    config.headers = config.headers || {};
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    } else {
      delete config.headers.Authorization;
    }
  }
  return config;
});

api.interceptors.response.use(
  response => response,
  async error => {
    const originalRequest = error?.config;
    const status = error?.response?.status;
    const serverError = error?.response?.data?.error;
    if (!originalRequest || originalRequest._authRetried || originalRequest.skipAuthRefresh ||
        authEndpoint(originalRequest.url)) return Promise.reject(error);

    // Apenas expiração/invalidação do JWT permite renovação automática.
    // Um 403 de permissão NÃO deve ser contornado renovando o token.
    const invalidJwt = (status === 403 &&
      typeof serverError === "string" && serverError.startsWith("Invalid token.")) ||
      (status === 401 && serverError === "ERR_SESSION_EXPIRED");
    if (!invalidJwt) return Promise.reject(error);

    originalRequest._authRetried = true;
    // Se outra chamada já renovou o token enquanto esta resposta estava
    // em trânsito, repetir com o token novo sem disparar outro refresh.
    const sentAuthorization = originalRequest.headers && originalRequest.headers.Authorization;
    const currentToken = getAccessToken();
    if (currentToken && sentAuthorization && sentAuthorization !== `Bearer ${currentToken}`) {
      return api(originalRequest);
    }
    try {
      await refreshSession();
      return api(originalRequest);
    } catch (refreshError) {
      return Promise.reject(refreshError);
    }
  }
);

export default api;
